import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Megaphone, CheckCircle2, MapPin, Camera, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CHANNELS = ["TV / CTV", "Radio", "Mailbox (Direct Mail)", "Online / Digital", "Social Media", "Billboard / Outdoor", "Other"];

export default function VolunteerPortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ subject_name: "", channel: "", location: "", note: "", reporter_name: "", reporter_email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [points, setPoints] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);

  const onPhoto = (file) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      setPhoto(canvas.toDataURL("image/jpeg", 0.8));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  };

  const load = () => {
    axios.get(`${API}/api/public/volunteer/${token}`)
      .then(r => setInfo(r.data))
      .catch(e => setError(e?.response?.data?.detail || "This volunteer link is invalid or has been disabled"));
  };
  useEffect(load, [token]);

  const submit = async () => {
    if (!form.subject_name || !form.channel) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/volunteer/${token}/report`, {
        ...form, photo_base64: photo, date: new Date().toISOString().slice(0, 10),
      });
      setDone(true);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit — try again");
      setTimeout(() => setError(null), 3000);
    } finally { setSubmitting(false); }
  };

  if (error && !info) return (
    <div className="min-h-screen bg-app text-white flex items-center justify-center p-6" data-testid="volunteer-error">
      <div className="text-center max-w-sm">
        <div className="font-display text-2xl font-bold mb-2">Link unavailable</div>
        <p className="text-sm text-slate-400">{error}</p>
      </div>
    </div>
  );
  if (!info) return <div className="min-h-screen bg-app text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  if (done) return (
    <div className="min-h-screen bg-app text-white flex items-center justify-center p-6" data-testid="volunteer-thanks">
      <div className="text-center max-w-sm">
        <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
        <div className="font-display text-2xl font-bold">Logged — thank you!</div>
        <p className="text-sm text-slate-400 mt-2">Your sighting went straight to the campaign team&apos;s opposition tracker.</p>
        <button onClick={() => { setDone(false); setPhoto(null); setForm({ ...form, channel: "", location: "", note: "" }); }}
          data-testid="log-another-btn"
          className="mt-6 w-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold py-3 text-sm">
          Log another sighting
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-app text-white" data-testid="volunteer-portal">
      <header className="border-b border-white/10 px-5 py-4">
        <div className="font-display font-bold tracking-tight text-sm">TotalReach <span className="brand-gradient">Volunteer Ad Watch</span></div>
      </header>
      <main className="max-w-md mx-auto px-5 py-6 space-y-5">
        <div>
          <h1 className="font-display text-2xl font-bold leading-tight">Spotted a political ad or mailer?</h1>
          <p className="text-sm text-slate-400 mt-1.5">
            Log it for the <span className="text-white">{info.candidate_name}</span> campaign ({info.office || "race"} · {info.state}{info.district && info.district !== "00" ? `-${info.district}` : ""}). Takes 20 seconds.
          </p>
          <div className="text-[10px] text-slate-600 mt-1">{info.reports_logged} sightings logged so far</div>
        </div>

        <div data-testid="volunteer-subject-group">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Whose ad was it?</div>
          <div className="grid grid-cols-1 gap-2">
            {info.subjects.map(s => (
              <button key={s} onClick={() => setForm({ ...form, subject_name: s })} data-testid={`volunteer-subject-${s}`}
                className={`rounded-xl border px-4 py-3 text-sm text-left transition-colors ${form.subject_name === s ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-300"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div data-testid="volunteer-channel-group">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Where did you see it?</div>
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map(ch => (
              <button key={ch} onClick={() => setForm({ ...form, channel: ch })} data-testid={`volunteer-channel-${ch}`}
                className={`rounded-full border px-3.5 py-2 text-xs transition-colors ${form.channel === ch ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-400"}`}>
                {ch}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> City or ZIP (optional)</label>
            <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
              placeholder="Austin, 78701…" data-testid="volunteer-location-input"
              className="w-full bg-slate-900/80 border border-white/15 rounded-xl px-4 py-3 text-sm focus:border-cyan-400/60 focus:outline-none placeholder-slate-600" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1.5">What did it say? (optional)</label>
            <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} maxLength={280}
              placeholder="Attack ad about taxes on Channel 7 evening news…" data-testid="volunteer-note-input"
              className="w-full bg-slate-900/80 border border-white/15 rounded-xl px-4 py-3 text-sm focus:border-cyan-400/60 focus:outline-none placeholder-slate-600" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 flex items-center gap-1"><Camera className="w-3 h-3" /> Photo of the ad or mailer (optional)</label>
            {photo ? (
              <div className="relative inline-block">
                <img src={photo} alt="sighting preview" className="rounded-xl max-h-40 border border-white/15" data-testid="volunteer-photo-preview" />
                <button onClick={() => setPhoto(null)} data-testid="volunteer-photo-remove"
                  className="absolute -top-2 -right-2 bg-slate-800 border border-white/20 rounded-full p-1 text-slate-300"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 px-4 py-5 text-xs text-slate-400 cursor-pointer hover:border-cyan-400/40 transition-colors" data-testid="volunteer-photo-label">
                <Camera className="w-4 h-4 text-cyan-300/70" /> Snap or upload a photo
                <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="volunteer-photo-input"
                  onChange={e => e.target.files[0] && onPhoto(e.target.files[0])} />
              </label>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1.5">Your name (optional)</label>
            <input value={form.reporter_name} onChange={e => setForm({ ...form, reporter_name: e.target.value })}
              placeholder="Sam the volunteer" data-testid="volunteer-name-input"
              className="w-full bg-slate-900/80 border border-white/15 rounded-xl px-4 py-3 text-sm focus:border-cyan-400/60 focus:outline-none placeholder-slate-600" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1.5">Your email (optional — earns you points)</label>
            <input type="email" value={form.reporter_email} onChange={e => setForm({ ...form, reporter_email: e.target.value })}
              placeholder="you@yourmail.com" data-testid="volunteer-email-input"
              className="w-full bg-slate-900/80 border border-white/15 rounded-xl px-4 py-3 text-sm focus:border-cyan-400/60 focus:outline-none placeholder-slate-600" />
            <div className="text-[10px] text-slate-600 mt-1">
              Add your email to track points on the volunteer leaderboard.
              {form.reporter_email && form.reporter_email.includes("@") && (
                <button onClick={async () => {
                  setPointsLoading(true);
                  try {
                    const r = await axios.post(`${API}/api/public/volunteer-points/lookup`, { email: form.reporter_email });
                    setPoints(r.data);
                  } catch { setPoints({ error: true }); } finally { setPointsLoading(false); }
                }}
                  data-testid="volunteer-my-points-btn"
                  className="ml-2 text-cyan-300 underline">Show my points</button>
              )}
            </div>
            {pointsLoading && <div className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading points…</div>}
            {points && !points.error && (
              <div className="mt-2 p-3 rounded-xl border border-cyan-400/30 bg-cyan-400/5" data-testid="volunteer-points-card">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg font-bold text-cyan-200">{points.points} pts</div>
                    <div className="text-xs text-slate-400">{points.tier?.label}{points.tier?.next && ` · ${(points.tier.next_at || 0) - points.points} pts to ${points.tier.next}`}</div>
                  </div>
                  <div className="text-[10px] text-slate-500 text-right leading-relaxed">
                    {points.breakdown?.shifts_upcoming || 0} shift{(points.breakdown?.shifts_upcoming || 0) === 1 ? "" : "s"} signed up<br />
                    {points.breakdown?.shifts_complete || 0} shift{(points.breakdown?.shifts_complete || 0) === 1 ? "" : "s"} worked<br />
                    {points.breakdown?.rsvps || 0} event RSVP{(points.breakdown?.rsvps || 0) === 1 ? "" : "s"} · {points.breakdown?.checkins || 0} check-in{(points.breakdown?.checkins || 0) === 1 ? "" : "s"}<br />
                    {points.breakdown?.sightings || 0} ad sighting{(points.breakdown?.sightings || 0) === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {error && <div className="text-xs text-rose-300">{error}</div>}

        <button onClick={submit} disabled={submitting || !form.subject_name || !form.channel} data-testid="volunteer-submit-btn"
          className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold py-3.5 text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
          Log this sighting
        </button>
        <div className="text-center text-[10px] text-slate-600 pb-6">Powered by TotalReach Political Intelligence™</div>
      </main>
    </div>
  );
}
